import React, { useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  Button,
  Checkbox,
  DatePicker,
  Dropdown,
  Iconography,
  IconographySizes,
} from "@hid-galaxy-ui/galaxy-react";
import type { StylesConfig } from "react-select";
import {
  Autocomplete,
  TextField as MuiTextField,
  Popper,
  Checkbox as MuiCheckbox,
  Chip,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import { CountryCode } from "../../../../../../utils/CountryCode";
import { getWorkPackagesSagaAction } from "../../../../../../redux/saga/external-contract/saga-actions";
import { WorkPackagesType } from "../../../../../../redux/slices/external-contract/Types";
import { Constants } from "../../../../../../utils/constants";
import {
  getPartnerVerticalSagaAction,
  getRevenueStreamSagaAction,
  getPartnerTypesSagaAction,
} from "../../../../../../redux/saga/partner-details/saga-actions";
import { getPartnerTierListSagaAction } from "../../../../../../redux/saga/partner-program/saga-actions";
import {
  buildPartnerTierFilterOptions,
} from "../partnerTierFilterUtils";
import { SUPPORT_LEVEL_OPTIONS } from "../../../../../../features/partner-dashboard/constants/supportLevel";
import FilterChipDeleteIcon from "../FilterChipDeleteIcon";
import "../../../../create-new-partner/partner-detail-form/PartnerDetailForm.scss";
import "./PartnerFilterModal.scss";

interface PartnerFilterModalProps {
  partnerFilterProp: {
    partnerName: string[];
    status: string[];
    type: string[];
    tier: string[];
    vertical: string[];
    supportLevel: string[];
    revenueStream: string[];
    accountManager: string[];
    region: string[];
    country: string[];
    contractName: string[];
    contractStatus: string[];
    startDateRange: string[];
    endDateRange: string[];
  };
  setPartnerFilterProp: React.Dispatch<React.SetStateAction<any>>;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface Option {
  label: string;
  value: string;
}

interface GroupedOption {
  label: string;
  value: string;
  group: string;
}

interface GroupedOptions {
  label: string;
  value: string;
  options?: Option[];
}

interface DateState {
  startDateFrom: Date | null;
  startDateTo: Date | null;
  endDateFrom: Date | null;
  endDateTo: Date | null;
}

const FILTER_DRAWER_Z_INDEX = 1300;
const PARTNER_MODAL_DROPDOWN_OPTION_HOVER_BG = "#e6e6e6";

const filterDropdownMenuStyles: StylesConfig<Option, true> = {
  menuPortal: (base) => ({
    ...base,
    zIndex: FILTER_DRAWER_Z_INDEX,
  }),
  menu: (base) => ({
    ...base,
    zIndex: FILTER_DRAWER_Z_INDEX + 1,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? PARTNER_MODAL_DROPDOWN_OPTION_HOVER_BG
      : state.isSelected
        ? base.backgroundColor
        : "transparent",
    color: base.color,
    ":active": {
      backgroundColor: PARTNER_MODAL_DROPDOWN_OPTION_HOVER_BG,
    },
  }),
};

const FilterAutocompletePopper = (
  props: React.ComponentProps<typeof Popper>
) => (
  <Popper
    {...props}
    style={{ ...props.style, zIndex: FILTER_DRAWER_Z_INDEX }}
    placement="bottom-start"
  />
);

const CountryOptions = [
  { label: "Global", value: "Global" },
  ...CountryCode.map((country) => ({
    label: country.name,
    value: country.name,
  })),
];

const transformWorkPackages = (
  workPackages: WorkPackagesType[]
): GroupedOptions[] => {
  const grouped: Record<string, Option[]> = workPackages.reduce(
    (acc: Record<string, Option[]>, pkg) => {
      const description = pkg.description.trim();
      if (!acc[description]) {
        acc[description] = [];
      }
      acc[description].push({
        label: pkg.value.trim(),
        value: pkg.value.trim(),
      });
      return acc;
    },
    {}
  );

  return Object.keys(grouped).map((key) => ({
    label: key,
    value: key,
    options: grouped[key],
  }));
};

const SearchableMultiSelectField: React.FC<{
  id: string;
  label: string;
  placeholder: string;
  options: string[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
}> = ({ id, label, placeholder, options, selectedValues, onChange }) => (
  <div className="label-field-container partner-searchable-multiselect partner-searchable-multiselect--tags-below">
    <label className="field-label" htmlFor={id}>
      {label}
    </label>
    <Autocomplete
      id={id}
      multiple
      disableCloseOnSelect
      options={options}
      value={selectedValues}
      onChange={(_, value) => onChange(value)}
      PopperComponent={FilterAutocompletePopper}
      popupIcon={
        <Iconography icon="angleDown" size={IconographySizes.Small} />
      }
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option}>
          <MuiCheckbox size="small" checked={selected} />
          {option}
        </li>
      )}
      renderTags={() => null}
      renderInput={(params) => (
        <MuiTextField {...params} placeholder={placeholder} />
      )}
    />
    {selectedValues.length > 0 && (
      <div className="partner-filter-selected-tags">
        {selectedValues.map((option) => (
          <Chip
            key={option}
            label={option}
            size="small"
            deleteIcon={<FilterChipDeleteIcon />}
            onDelete={() =>
              onChange(selectedValues.filter((value) => value !== option))
            }
          />
        ))}
      </div>
    )}
  </div>
);

const GroupedMultiSelectField: React.FC<{
  id: string;
  label: string;
  placeholder: string;
  options: GroupedOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
}> = ({ id, label, placeholder, options, selectedValues, onChange }) => {
  const selectedOptions = options.filter((option) =>
    selectedValues.includes(option.value)
  );

  return (
    <div className="label-field-container partner-searchable-multiselect partner-searchable-multiselect--tags-below">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <Autocomplete
        id={id}
        multiple
        disableCloseOnSelect
        options={options}
        groupBy={(option) => option.group}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        value={selectedOptions}
        onChange={(_, value) => onChange(value.map((item) => item.value))}
        PopperComponent={FilterAutocompletePopper}
        popupIcon={
          <Iconography icon="angleDown" size={IconographySizes.Small} />
        }
        renderOption={(props, option, { selected }) => (
          <li {...props} key={option.value}>
            <MuiCheckbox size="small" checked={selected} />
            {option.label}
          </li>
        )}
        renderTags={() => null}
        renderInput={(params) => (
          <MuiTextField {...params} placeholder={placeholder} />
        )}
      />
      {selectedValues.length > 0 && (
        <div className="partner-filter-selected-tags">
          {selectedValues.map((value) => {
            const option = options.find((item) => item.value === value);
            return (
              <Chip
                key={value}
                label={option?.label ?? value}
                size="small"
                deleteIcon={<FilterChipDeleteIcon />}
                onDelete={() =>
                  onChange(selectedValues.filter((item) => item !== value))
                }
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const FilterCheckbox: React.FC<{
  label: string;
  checked: boolean;
  onToggle: () => void;
}> = ({ label, checked, onToggle }) => (
  <div
    className="partner-filter-drawer__checkbox-item"
    role="button"
    tabIndex={0}
    onClick={onToggle}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onToggle();
      }
    }}
  >
    <Checkbox
      name={label}
      value={label}
      checked={checked}
      id={`filter-checkbox-${label}`}
      onChange={() => {}}
    />
  </div>
);

const FilterSection: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <section className="partner-form-section">
    <h3 className="partner-form-section__title">{title}</h3>
    <div className="partner-form-section__card">{children}</div>
  </section>
);

const PartnerFilterModal: React.FC<PartnerFilterModalProps> = ({
  partnerFilterProp,
  setPartnerFilterProp,
  setModalOpen,
}) => {
  const dispatch = useAppDispatch();

  const {
    partnerTypes,
    partnerVertical,
    allPartners,
    revenueStream,
  } = useAppSelector((state) => state.partner_details);
  const { workPackages } = useAppSelector((state) => state.external_contract);
  const { partnerTierList } = useAppSelector((state) => state.partner_program);

  const [partnerFilter, setPartnerFilter] = React.useState(partnerFilterProp);
  const [dates, setDates] = React.useState<DateState>(() => {
    const dateObject = (date: string | undefined) =>
      date ? new Date(date) : null;
    return {
      startDateFrom: dateObject(partnerFilterProp.startDateRange[0]),
      startDateTo: dateObject(partnerFilterProp.startDateRange[1]),
      endDateFrom: dateObject(partnerFilterProp.endDateRange[0]),
      endDateTo: dateObject(partnerFilterProp.endDateRange[1]),
    };
  });

  const partnerStatus = useMemo(
    () =>
      Constants.PARTNER_STATUS.map((status) => ({
        label: status,
        value: status,
      })),
    []
  );

  const types = useMemo(
    () =>
      partnerTypes.map((type) => ({
        label: type.value,
        value: type.value,
      })),
    [partnerTypes]
  );

  const tierOptions = useMemo(
    () => buildPartnerTierFilterOptions(partnerTierList),
    [partnerTierList]
  );

  const verticals = useMemo(
    () =>
      partnerVertical.map((vertical) => ({
        label: vertical.value,
        value: vertical.value,
      })),
    [partnerVertical]
  );

  const supportLevelOptions = useMemo(
    () =>
      SUPPORT_LEVEL_OPTIONS.map((level) => ({
        label: level,
        value: level,
      })),
    []
  );

  const accountManagerOptions = useMemo(
    () =>
      Array.from(
        new Set(allPartners.map((p) => p.account_manager).filter(Boolean))
      ).map((manager) => ({
        label: manager,
        value: manager,
      })),
    [allPartners]
  );

  const partnerOptions = useMemo(
    () =>
      allPartners.map((partner) => ({
        label: partner.partner_name,
        value: partner.partner_name,
      })),
    [allPartners]
  );

  const revenueStreamOptions = useMemo(
    () =>
      revenueStream.map((stream) => ({
        label: stream.value,
        value: stream.value,
      })),
    [revenueStream]
  );

  const workPackageTypeOptions = useMemo(
    () => transformWorkPackages(workPackages),
    [workPackages]
  );

  const flatWorkPackageOptions = useMemo(
    () =>
      workPackageTypeOptions.flatMap((group) =>
        (group.options ?? []).map((option) => ({
          label: option.label,
          value: option.value,
          group: group.label,
        }))
      ),
    [workPackageTypeOptions]
  );

  const selectedPartnerNames = useMemo(
    () =>
      partnerFilter.partnerName.map((name) => ({ label: name, value: name })),
    [partnerFilter.partnerName]
  );

  const selectedTierOptions = useMemo(
    () =>
      partnerFilter.tier
        .map((tierId) => tierOptions.find((option) => option.value === tierId))
        .filter((option): option is Option => Boolean(option)),
    [partnerFilter.tier, tierOptions]
  );

  React.useEffect(() => {
    if (partnerTypes.length === 0) dispatch(getPartnerTypesSagaAction());
    if (workPackages.length === 0) dispatch(getWorkPackagesSagaAction());
    if (revenueStream.length === 0) dispatch(getRevenueStreamSagaAction());
    if (partnerTierList.length === 0) dispatch(getPartnerTierListSagaAction(0));
    if (partnerVertical.length === 0) dispatch(getPartnerVerticalSagaAction());
  }, [
    dispatch,
    partnerTypes,
    workPackages,
    revenueStream,
    partnerTierList,
    partnerVertical,
  ]);

  React.useEffect(() => {
    document.body.classList.add("partner-filter-drawer-open");
    return () => {
      document.body.classList.remove("partner-filter-drawer-open");
    };
  }, []);

  const onCheckboxClick = useCallback(
    (
      eventResult: { checked: boolean; value: string },
      filterName: keyof typeof partnerFilter
    ) => {
      setPartnerFilter((prevState) => {
        const currentValues = prevState[filterName];

        if (eventResult.checked) {
          return currentValues.includes(eventResult.value)
            ? prevState
            : {
                ...prevState,
                [filterName]: [...currentValues, eventResult.value],
              };
        }
        return {
          ...prevState,
          [filterName]: currentValues.filter(
            (val) => val !== eventResult.value
          ),
        };
      });
    },
    []
  );

  const formatDateForDB = useCallback((date: Date | null) => {
    return date ? date.toLocaleDateString("en-CA") : null;
  }, []);

  const handleDateChange = useCallback(
    (date: Date | [Date | null, Date | null] | null, name: keyof DateState) => {
      const actualDate = Array.isArray(date) ? date[0] : date;

      setDates((prev) => ({ ...prev, [name]: actualDate }));

      const formattedDate = formatDateForDB(actualDate);

      setPartnerFilter((prev) => {
        const newFilter = { ...prev };

        switch (name) {
          case "startDateFrom":
            newFilter.startDateRange = [formattedDate, prev.startDateRange[1]];
            if (!formattedDate && !prev.startDateRange[1]) {
              newFilter.startDateRange = [];
            }
            break;
          case "startDateTo":
            newFilter.startDateRange = [prev.startDateRange[0], formattedDate];
            if (!prev.startDateRange[0] && !formattedDate) {
              newFilter.startDateRange = [];
            }
            break;
          case "endDateFrom":
            newFilter.endDateRange = [formattedDate, prev.endDateRange[1]];
            if (!formattedDate && !prev.endDateRange[1]) {
              newFilter.endDateRange = [];
            }
            break;
          case "endDateTo":
            newFilter.endDateRange = [prev.endDateRange[0], formattedDate];
            if (!prev.endDateRange[0] && !formattedDate) {
              newFilter.endDateRange = [];
            }
            break;
        }
        return newFilter;
      });
    },
    [formatDateForDB]
  );

  const onClearAllClick = useCallback(() => {
    const emptyFilter: typeof partnerFilterProp = {
      partnerName: [],
      status: [],
      type: [],
      tier: [],
      vertical: [],
      supportLevel: [],
      revenueStream: [],
      accountManager: [],
      region: [],
      country: [],
      contractName: [],
      contractStatus: [],
      startDateRange: [],
      endDateRange: [],
    };

    setPartnerFilter(emptyFilter);
    setDates({
      startDateFrom: null,
      startDateTo: null,
      endDateFrom: null,
      endDateTo: null,
    });
  }, []);

  const onApplyClick = useCallback(() => {
    const updatedFilter = { ...partnerFilter };

    if (updatedFilter.startDateRange[0] && !updatedFilter.startDateRange[1]) {
      updatedFilter.startDateRange[1] = updatedFilter.startDateRange[0];
    } else if (
      !updatedFilter.startDateRange[0] &&
      updatedFilter.startDateRange[1]
    ) {
      updatedFilter.startDateRange[0] = updatedFilter.startDateRange[1];
    }

    if (updatedFilter.endDateRange[0] && !updatedFilter.endDateRange[1]) {
      updatedFilter.endDateRange[1] = updatedFilter.endDateRange[0];
    } else if (
      !updatedFilter.endDateRange[0] &&
      updatedFilter.endDateRange[1]
    ) {
      updatedFilter.endDateRange[0] = updatedFilter.endDateRange[1];
    }
    setPartnerFilterProp(updatedFilter);
    setModalOpen(false);
  }, [partnerFilter, setPartnerFilterProp, setModalOpen]);

  const handleDropdownChange = useCallback(
    (data: Option[], filterName: keyof typeof partnerFilter) => {
      setPartnerFilter((prev) => ({
        ...prev,
        [filterName]: data.map((item) => item.value),
      }));
    },
    []
  );

  const handleMUISelectChange = useCallback(
    (data: string[], filterName: keyof typeof partnerFilter) => {
      setPartnerFilter((prev) => ({
        ...prev,
        [filterName]: data,
      }));
    },
    []
  );

  return createPortal(
    <>
      <div className="partner-filter-drawer__backdrop" aria-hidden="true" />
      <div
        className="partner-filter-drawer partner-filter-drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Filter Partners"
      >
        <div className="partner-filter-drawer__header">
        <div className="partner-filter-drawer__header-title">
          <span className="partner-filter-drawer__filter-icon" aria-hidden="true">
            <Iconography icon="sliders" size={IconographySizes.Medium} />
          </span>
          <span>Filter Partners</span>
        </div>
        <button
          type="button"
          className="partner-filter-drawer__close-btn"
          onClick={() => setModalOpen(false)}
          aria-label="Close filter panel"
        >
          <Iconography icon="xmark" size={IconographySizes.Medium} />
        </button>
      </div>

      <div className="partner-filter-drawer__body partner-detail-form-container partner-detail-form-container--modal">
        <FilterSection title="Basic Information">
          <div className="label-field-container">
            <label className="field-label" htmlFor="partnerNames">
              Search or select Partners
            </label>
            <Dropdown
              label=""
              id="partnerNames"
              placeholder="Partners"
              options={partnerOptions}
              isClearable={true}
              isMulti={true}
              isSearchable={true}
              selected={selectedPartnerNames}
              onSelect={(data) => handleDropdownChange(data, "partnerName")}
              menuPortalTarget={document.body}
              styles={filterDropdownMenuStyles}
            />
          </div>

          <SearchableMultiSelectField
            id="partnerStatus"
            label="Partner Status"
            placeholder="Select"
            selectedValues={partnerFilter.status}
            options={partnerStatus.map((option) => option.value)}
            onChange={(values) => handleMUISelectChange(values, "status")}
          />

          <SearchableMultiSelectField
            id="partnerType"
            label="Partner Type"
            placeholder="Select"
            selectedValues={partnerFilter.type}
            options={types.map((option) => option.value)}
            onChange={(values) => handleMUISelectChange(values, "type")}
          />

          <div className="label-field-container">
            <label className="field-label" htmlFor="partnerTier">
              Partner Tier
            </label>
            <Dropdown
              label=""
              id="partnerTier"
              placeholder="Select"
              options={tierOptions}
              isClearable={true}
              isMulti={true}
              isSearchable={true}
              selected={selectedTierOptions}
              onSelect={(data) => handleDropdownChange(data, "tier")}
              menuPortalTarget={document.body}
              styles={filterDropdownMenuStyles}
            />
          </div>

          <SearchableMultiSelectField
            id="partnerVertical"
            label="Partner Vertical"
            placeholder="Select"
            selectedValues={partnerFilter.vertical}
            options={verticals.map((option) => option.value)}
            onChange={(values) => handleMUISelectChange(values, "vertical")}
          />

          <SearchableMultiSelectField
            id="supportLevel"
            label="Support Level"
            placeholder="Select"
            selectedValues={partnerFilter.supportLevel}
            options={supportLevelOptions.map((option) => option.value)}
            onChange={(values) => handleMUISelectChange(values, "supportLevel")}
          />
        </FilterSection>

        <FilterSection title="HID Account Manager">
          <SearchableMultiSelectField
            id="accountManager"
            label="HID Account Manager"
            placeholder="Select"
            selectedValues={partnerFilter.accountManager}
            options={accountManagerOptions.map((option) => option.value)}
            onChange={(values) =>
              handleMUISelectChange(values, "accountManager")
            }
          />
        </FilterSection>

        <FilterSection title="Revenue Stream">
          <SearchableMultiSelectField
            id="revenueStream"
            label="Revenue Stream"
            placeholder="Select"
            selectedValues={partnerFilter.revenueStream}
            options={revenueStreamOptions.map((option) => option.value)}
            onChange={(values) =>
              handleMUISelectChange(values, "revenueStream")
            }
          />
        </FilterSection>

        <FilterSection title="Region & Country">
          <div className="partner-filter-drawer__checkbox-grid partner-filter-drawer__checkbox-grid--region">
            {Constants.REGIONS.map((item) => (
              <FilterCheckbox
                key={item}
                label={item}
                checked={partnerFilter.region.includes(item)}
                onToggle={() =>
                  onCheckboxClick(
                    {
                      checked: !partnerFilter.region.includes(item),
                      value: item,
                    },
                    "region"
                  )
                }
              />
            ))}
          </div>

          <SearchableMultiSelectField
            id="country"
            label="Country"
            placeholder="Select"
            selectedValues={partnerFilter.country}
            options={CountryOptions.map((option) => option.value)}
            onChange={(values) => handleMUISelectChange(values, "country")}
          />
        </FilterSection>

        <FilterSection title="Contract Details">
          <div className="partner-filter-drawer__checkbox-grid partner-filter-drawer__checkbox-grid--three-col">
            {Constants.CONTRACT_STATUS.map((item) => (
              <FilterCheckbox
                key={item}
                label={item}
                checked={partnerFilter.contractStatus.includes(item)}
                onToggle={() =>
                  onCheckboxClick(
                    {
                      checked: !partnerFilter.contractStatus.includes(item),
                      value: item,
                    },
                    "contractStatus"
                  )
                }
              />
            ))}
          </div>

          <GroupedMultiSelectField
            id="contractName"
            label="Work Package Type"
            placeholder="Select"
            options={flatWorkPackageOptions}
            selectedValues={partnerFilter.contractName}
            onChange={(values) => handleMUISelectChange(values, "contractName")}
          />

          <div className="partner-form-grid">
            <div className="partner-registration-date-field">
              <label className="field-label" htmlFor="startDateFrom">
                Start Date From
              </label>
              <DatePicker
                label=""
                onChange={(date) => handleDateChange(date, "startDateFrom")}
                format="dd/MM/yyyy"
                placeholder="dd/mm/yyyy"
                id="startDateFrom"
                name="startDateFrom"
                value={dates.startDateFrom}
                isClearable
              />
            </div>
            <div className="partner-registration-date-field">
              <label className="field-label" htmlFor="startDateTo">
                Start Date To
              </label>
              <DatePicker
                label=""
                onChange={(date) => handleDateChange(date, "startDateTo")}
                format="dd/MM/yyyy"
                placeholder="dd/mm/yyyy"
                id="startDateTo"
                name="startDateTo"
                value={dates.startDateTo}
                isClearable
              />
            </div>
            <div className="partner-registration-date-field">
              <label className="field-label" htmlFor="endDateFrom">
                End Date From
              </label>
              <DatePicker
                label=""
                onChange={(date) => handleDateChange(date, "endDateFrom")}
                format="dd/MM/yyyy"
                placeholder="dd/mm/yyyy"
                id="endDateFrom"
                name="endDateFrom"
                value={dates.endDateFrom}
                isClearable
              />
            </div>
            <div className="partner-registration-date-field">
              <label className="field-label" htmlFor="endDateTo">
                End Date To
              </label>
              <DatePicker
                label=""
                onChange={(date) => handleDateChange(date, "endDateTo")}
                format="dd/MM/yyyy"
                placeholder="dd/mm/yyyy"
                id="endDateTo"
                name="endDateTo"
                value={dates.endDateTo}
                isClearable
              />
            </div>
          </div>
        </FilterSection>
      </div>

      <div className="partner-filter-drawer__footer">
        <Button
          variant="secondary"
          label="CANCEL"
          onClick={() => setModalOpen(false)}
        />
        <div className="partner-filter-drawer__footer-right">
          <Button
            variant="secondary"
            label="RESET"
            onClick={onClearAllClick}
          />
          <Button variant="primary" label="APPLY" onClick={onApplyClick} />
        </div>
      </div>
      </div>
    </>,
    document.body
  );
};

export default PartnerFilterModal;
